import { dashboardPaint, dashboardCss, dashboardActionForeground } from '../utils/dashboardTheme';
// ✅ FIXED: Complete AssignUser.jsx with NO auto-assignment of assessments to new users
// src/components/AssignUser.jsx
import React, { useState, useEffect } from 'react';
import { 
  UserPlus, 
  Mail, 
  Lock, 
  Calendar, 
  Shield, 
  Users, 
  Clock,
  AlertTriangle, 
  CheckCircle,
  Info,
  Ban,
  XCircle,
  Loader2
} from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';
import { validateEmail, validatePinCode } from '../utils/adminUtils';
import { api } from '../api/client';

// EST Time Formatting Functions
const getCurrentDateTimeEST = () => {
  try {
    const now = new Date();
    const estFormatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    });
    
    return estFormatter.format(now);
  } catch (error) {
    console.error('Error formatting current date/time to EST:', error);
    return new Date().toLocaleString();
  }
};

const calculateValidityEndDateEST = (validityDays) => {
  try {
    const days = parseInt(validityDays);
    if (isNaN(days) || days <= 0) return '';
    
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + days);
    
    // Format the end date in EST/EDT timezone
    const estFormatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/New_York'
    });
    
    return estFormatter.format(endDate);
  } catch (error) {
    console.error('Error calculating validity end date:', error);
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + parseInt(validityDays || 300));
    return endDate.toLocaleDateString();
  }
};

const formatTimeToEST = (timeString) => {
  if (!timeString) return '';
  
  try {
    const today = new Date().toISOString().split('T')[0];
    const dateTime = new Date(`${today} ${timeString}`);
    
    if (isNaN(dateTime.getTime())) {
      return timeString;
    }
    
    // Convert to EST/EDT using proper timezone
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    });
    
    return timeFormatter.format(dateTime);
  } catch (error) {
    console.error('Error formatting time to EST:', error);
    return timeString;
  }
};

export default function AssignUser() {
  const { assignUser } = useAdmin();
  
  // Performance loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingSubUsers, setIsCreatingSubUsers] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    pinCode: '',
    accessCode: '',
    validityDays: '300', 
    isEnabled: true,     
    restrictDays: true,  
    daysAllowed: ["1", "2", "3", "4", "5"], 
    blockAllDays: false,
    subUserCount: 0,
    maxSubUsers: 5,
    restrictTime: false,
    accessStartTime: '07:00',
    accessEndTime: '15:00'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Responsive design state
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const daysOfWeek = [
    { label: 'Monday', value: "1" },
    { label: 'Tuesday', value: "2" },
    { label: 'Wednesday', value: "3" },
    { label: 'Thursday', value: "4" },
    { label: 'Friday', value: "5" },
    { label: 'Saturday', value: "6" },
    { label: 'Sunday', value: "7" }
  ];

  const handleDayChange = (e, dayValue) => {
    if (e.target.checked) {
      if (!formData.daysAllowed.includes(dayValue)) {
        setFormData({ ...formData, daysAllowed: [...formData.daysAllowed, dayValue] });
      }
    } else {
       setFormData({ ...formData, daysAllowed: formData.daysAllowed.filter(day => day !== dayValue) });
    }
  };

  const handleBlockAllDaysChange = (e) => {
    const isBlocked = e.target.checked;
    setFormData({ 
      ...formData, 
      blockAllDays: isBlocked,
      daysAllowed: isBlocked ? [] : ["1", "2", "3", "4", "5"]
    });
  };

  const handleSelectAllDays = () => {
    setFormData({ 
      ...formData, 
      daysAllowed: ["1", "2", "3", "4", "5", "6", "7"],
      blockAllDays: false
    });
  };

  const handleClearAllDays = () => {
    setFormData({ 
      ...formData, 
      daysAllowed: [],
      blockAllDays: true
    });
  };

  // Background SubUser creation function (non-blocking)
  const createSubUsersInBackground = async (mainUserId, subUserCount, creationTime, accessInfo) => {
    try {
      setIsCreatingSubUsers(true);
      
      const subUsersResponse = await api.createSubUsers({
        mainUserId: mainUserId,
        count: parseInt(subUserCount) || 1
      });
      
      const actualSubUserCount = Array.isArray(subUsersResponse) ? subUsersResponse.length : (subUsersResponse.createdSubUsers?.length || 0);
      
      // Update success message with SubUser completion
      setSuccess(
        `✅ User and ${actualSubUserCount} SubUsers created successfully at ${creationTime} EST! ` +
        `Account expires ${calculateValidityEndDateEST(formData.validityDays)} EST.${accessInfo}`
      );
      
      // Clear success message after extended time
      setTimeout(() => {
        setSuccess('');
      }, 5000);
      
    } catch (subUserErr) {
      console.error("❌ [AssignUser] Error creating subusers:", subUserErr);
      
      // Update with partial success message
      setSuccess(
        `✅ User created successfully at ${creationTime} EST but some SubUsers failed to create. ` +
        `Account expires ${calculateValidityEndDateEST(formData.validityDays)} EST.${accessInfo} ` +
        `⚠️ SubUser creation error: ${subUserErr.message || 'Unknown error'}`
      );
      
      setTimeout(() => {
        setSuccess('');
      }, 6000);
    } finally {
      setIsCreatingSubUsers(false);
    }
  };

  // ✅ FIXED: Enhanced handleSubmit with empty assessment settings creation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter a name');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!validatePinCode(formData.pinCode)) {
      setError('PIN code must be 4-6 digits');
      return;
    }

    if (!formData.accessCode.trim()) {
      setError('Please enter an access code');
      return;
    }
    
    // ✅ ADDED: Validate access code format (alphanumeric, no spaces, min 4 chars)
    const accessCodeRegex = /^[A-Za-z0-9_-]{4,124}$/;
    if (!accessCodeRegex.test(formData.accessCode.trim())) {
      setError('Access code must contain 4-124 letters, numbers, underscores, or hyphens');
      return;
    }
    
    const validityDays = parseInt(formData.validityDays);
    if (isNaN(validityDays) || validityDays <= 0) {
      setError('Validity days must be a positive number');
      return;
    }

    // Time validation for restricted access
    if (formData.restrictTime) {
      if (!formData.accessStartTime || !formData.accessEndTime) {
        setError('Please set both start and end times for restricted access');
        return;
      }
      
      const startTime = new Date(`2000-01-01 ${formData.accessStartTime}`);
      const endTime = new Date(`2000-01-01 ${formData.accessEndTime}`);
      
      if (startTime >= endTime) {
        setError('End time must be after start time');
        return;
      }
    }

    // ✅ ADDED: Validate subUserCount doesn't exceed maxSubUsers
    if (formData.subUserCount > 0 && formData.subUserCount > formData.maxSubUsers) {
      setError(`Cannot create ${formData.subUserCount} SubUsers. Maximum allowed is ${formData.maxSubUsers}.`);
      return;
    }

    try {   
      setIsSubmitting(true);
      
      const payload = {
        ...formData,
        daysAllowed: formData.blockAllDays ? "" : (formData.daysAllowed.length > 0 ? formData.daysAllowed.join(",") : "1,2,3,4,5"),
      };

      // Show progress to user
      setSuccess('🔄 Creating user account...');

      // Settings are created only when an assessment is actually assigned.
      // This keeps account creation fast and avoids a user × catalog matrix.
      const newUser = await assignUser(payload);
      
      const creationTime = getCurrentDateTimeEST();

      // Enhanced success message with access restriction info
      let accessInfo = '';
      if (formData.blockAllDays) {
        accessInfo = ' ⚠️ User has been created with COMPLETE ACCESS BLOCK - no assessment access on any day.';
      } else if (formData.daysAllowed.length < 7) {
        const dayNames = formData.daysAllowed.map(d => {
          const dayMap = {'1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun'};
          return dayMap[d];
        }).join(', ');
        accessInfo = ` Access allowed on: ${dayNames}.`;
      }

      // Handle SubUsers in background (non-blocking)
      if (formData.subUserCount > 0 && newUser && newUser.id) {
        // Show immediate success for main user
        setSuccess(
          `✅ User created successfully at ${creationTime} EST with NO assessments assigned! ` +
          `Account expires ${calculateValidityEndDateEST(formData.validityDays)} EST.${accessInfo} ` +
          `🔄 Creating ${formData.subUserCount} SubUsers in background...`
        );

        // Create SubUsers in background without blocking UI
        createSubUsersInBackground(newUser.id, formData.subUserCount, creationTime, accessInfo);
      } else {
        // Immediate success
        setSuccess(
          `✅ User successfully created at ${creationTime} EST with NO assessments assigned! ` +
          `Account expires ${calculateValidityEndDateEST(formData.validityDays)} EST.${accessInfo}`
        );
        
        // Clear message after normal timeout
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      }
      
      // Reset form immediately after user creation
      setFormData({
        name: '',
        email: '',
        pinCode: '',
        accessCode: '',
        validityDays: '300',
        isEnabled: true,
        restrictDays: true,
        daysAllowed: ["1", "2", "3", "4", "5"],
        blockAllDays: false,
        subUserCount: 0,
        maxSubUsers: 5,
        restrictTime: false,
        accessStartTime: '07:00',
        accessEndTime: '15:00'
      });
      
    } catch (err) {
      console.error('❌ [AssignUser] Error assigning user:', err);
      
      // Enhanced error handling with specific error messages
      let errorMessage = 'Failed to assign user. Please try again.';
      
      if (err.message) {
        if (err.message.includes('Email address already exists')) {
          errorMessage = 'This email address is already in use. Please use a different email.';
        } else if (err.message.includes('Access code already exists')) {
          errorMessage = 'This access code is already in use. Please use a different access code.';
        } else if (err.message.includes('constraint')) {
          errorMessage = 'A user with these credentials already exists. Please check email and access code.';
        } else {
          errorMessage = `Failed to assign user: ${err.message}`;
        }
      }
      
      setError(errorMessage);
      
      // Auto-clear error after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to format the validity preview
  const formatValidityPreview = () => {
    if (!formData.validityDays) return '';
    const endDate = calculateValidityEndDateEST(formData.validityDays);
    return endDate ? `Account will expire on ${endDate} EST` : '';
  };

  // Helper to format time range preview
  const formatTimeRangePreview = () => {
    if (!formData.restrictTime) return '';
    if (!formData.accessStartTime || !formData.accessEndTime) return '';
    
    const startFormatted = formatTimeToEST(formData.accessStartTime);
    const endFormatted = formatTimeToEST(formData.accessEndTime);
    
    return `Access allowed ${startFormatted} - ${endFormatted} EST`;
  };

  // Helper to format days access preview
  const formatDaysAccessPreview = () => {
    if (formData.blockAllDays) {
      return '🚫 COMPLETE ACCESS BLOCK - No assessment access on any day';
    }
    if (formData.daysAllowed.length === 0) {
      return '🚫 No days selected - Assessment access blocked';
    }
    if (formData.daysAllowed.length === 7) {
      return '✅ All days allowed';
    }
    const dayNames = formData.daysAllowed.map(d => {
      const dayMap = {'1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday', '5': 'Friday', '6': 'Saturday', '7': 'Sunday'};
      return dayMap[d];
    }).join(', ');
    return `Access allowed on: ${dayNames}`;
  };

  // Optimized button style with loading state
  const getButtonStyle = () => {
    const baseStyle = {
      width: '100%',
      padding: '14px 20px',
      color: dashboardPaint('white', 'text', true),
      border: dashboardPaint('none', 'border', true),
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: isSubmitting ? 'not-allowed' : 'pointer',
      marginTop: '24px',
      transition: 'all 0.3s ease',
      boxShadow: dashboardPaint('0 4px 6px -1px rgba(0, 0, 0, 0.1)', 'shadow', true),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      opacity: isSubmitting ? 0.7 : 1
    };

    if (isSubmitting) {
      return {
        ...baseStyle,
        background: dashboardPaint('linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('linear-gradient(135deg, #94a3b8 0%, #64748b 100%)')
      };
    }

    return {
      ...baseStyle,
      background: dashboardPaint('linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)')
    };
  };

  // ------------------ Styles ------------------

  const containerStyle = {
    padding: isMobileView ? '16px' : '24px',
    minHeight: '100vh',
  };

  const mainCard = {
    maxWidth: '900px',
    margin: '0 auto',
    background: dashboardPaint('rgba(15, 23, 42, 0.95)', 'background', false),
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    boxShadow: dashboardPaint('0 25px 50px -12px rgba(0, 0, 0, 0.4)', 'shadow', false),
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.2)', 'border', false),
    overflow: 'hidden',
    color: dashboardPaint('#e2e8f0', 'text', false)
  };

  const headerStyle = {
    background: dashboardPaint('linear-gradient(135deg, #1e293b 0%, #334155 100%)', 'background', false),
    padding: isMobileView ? '24px' : '32px',
    borderBottom: dashboardPaint('1px solid rgba(148, 163, 184, 0.1)', 'border', false)
  };

  const headerContent = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px'
  };

  const titleStyle = {
    fontSize: isMobileView ? '24px' : '32px',
    fontWeight: '700',
    color: dashboardPaint('#ffffff', 'text', false),
    margin: 0,
    letterSpacing: '-0.025em'
  };

  const subtitleStyle = {
    fontSize: isMobileView ? '14px' : '16px',
    color: dashboardPaint('rgba(255, 255, 255, 0.8)', 'text', false),
    margin: 0,
    lineHeight: 1.6
  };

  const contentStyle = {
    padding: isMobileView ? '24px' : '32px'
  };

  const formContainerStyle = {
    backgroundColor: dashboardPaint('rgba(30, 41, 59, 0.8)', 'background', false),
    borderRadius: '16px',
    boxShadow: dashboardPaint('0 10px 15px -3px rgba(0, 0, 0, 0.3)', 'shadow', false),
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.2)', 'border', false),
    padding: isMobileView ? '20px' : '24px',
  };

  const formRowStyle = {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: isMobileView ? 'column' : 'row',
    gap: '16px',
    width: '100%',
  };

  const formGroupStyle = {
    flex: 1, 
    minWidth: isMobileView ? 'auto' : '200px',
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    color: dashboardPaint('#e2e8f0', 'text', false),
    fontWeight: '500',
    fontSize: '14px',
  };

  const inputStyle = {
    width: '97%',
    padding: '12px',
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.3)', 'border', false),
    borderRadius: '12px',
    fontSize: '14px',
    backgroundColor: dashboardPaint('rgba(15, 23, 42, 0.8)', 'background', false),
    color: dashboardPaint('#ffffff', 'text', false),
    height: '42px',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      borderColor: dashboardPaint('#3b82f6', 'border', false),
      outline: dashboardPaint('none', 'border', false)
    }
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: dashboardPaint('rgba(15, 23, 42, 0.6)', 'background', false),
    borderRadius: '12px',
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.2)', 'border', false),
  };
  
  const checkboxStyle = {
    width: '18px',
    height: '18px',
    marginRight: '12px',
    accentColor: '#3b82f6'
  };

  // Special styling for block all days option
  const blockAllDaysContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: dashboardPaint(formData.blockAllDays ? 'rgba(127, 29, 29, 0.3)' : 'rgba(15, 23, 42, 0.6)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground(formData.blockAllDays ? 'rgba(127, 29, 29, 0.3)' : 'rgba(15, 23, 42, 0.6)'),
    borderRadius: '12px',
    border: dashboardPaint(formData.blockAllDays ? '2px solid rgba(220, 38, 38, 0.4)' : '1px solid rgba(148, 163, 184, 0.2)', 'border', true),
    boxShadow: dashboardPaint(formData.blockAllDays ? '0 0 10px rgba(220, 38, 38, 0.2)' : 'none', 'shadow', true)
  };

  // Quick action buttons for day selection
  const quickActionButtonStyle = {
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '500',
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.3)', 'border', true),
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: dashboardPaint('#e2e8f0', 'text', true),
    backgroundColor: dashboardPaint('rgba(30, 41, 59, 0.8)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(30, 41, 59, 0.8)')
  };

  const errorStyle = {
    backgroundColor: dashboardPaint('rgba(239, 68, 68, 0.2)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(239, 68, 68, 0.2)'),
    border: dashboardPaint('1px solid rgba(239, 68, 68, 0.4)', 'border', true),
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: dashboardPaint('#ef4444', 'text', true)
  };

  const successStyle = {
    backgroundColor: dashboardPaint('rgba(16, 185, 129, 0.2)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(16, 185, 129, 0.2)'),
    border: dashboardPaint('1px solid rgba(16, 185, 129, 0.4)', 'border', true),
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: dashboardPaint('#10b981', 'text', true)
  };

  const infoBoxStyle = {
    padding: '16px 20px', 
    backgroundColor: dashboardPaint('rgba(6,78,59,.3)', 'background', false), 
    borderRadius: '12px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    border: dashboardPaint('1px solid rgba(16,185,129,.3)', 'border', false)
  };

  const previewBoxStyle = {
    padding: '12px 16px',
    backgroundColor: dashboardPaint('rgba(59, 130, 246, 0.1)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(59, 130, 246, 0.1)'),
    borderRadius: '8px',
    border: dashboardPaint('1px solid rgba(59, 130, 246, 0.3)', 'border', true),
    marginTop: '8px',
    fontSize: '12px',
    color: dashboardPaint('#93c5fd', 'text', true)
  };

  // Special preview styling for blocked access
  const getPreviewBoxStyle = () => {
    if (formData.blockAllDays || formData.daysAllowed.length === 0) {
      return {
        ...previewBoxStyle,
        backgroundColor: dashboardPaint('rgba(239, 68, 68, 0.1)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(239, 68, 68, 0.1)'),
        borderColor: dashboardPaint('rgba(239, 68, 68, 0.3)', 'border', true),
        color: dashboardPaint('#f87171', 'text', true)
      };
    }
    return previewBoxStyle;
  };

  const daysSectionStyle = {
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: dashboardPaint(formData.blockAllDays ? 'rgba(30, 41, 59, 0.4)' : 'rgba(15, 23, 42, 0.6)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground(formData.blockAllDays ? 'rgba(30, 41, 59, 0.4)' : 'rgba(15, 23, 42, 0.6)'),
    borderRadius: '12px',
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.2)', 'border', true),
    opacity: formData.blockAllDays ? 0.6 : 1,
    transition: 'all 0.3s ease'
  };

  const daysGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobileView ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
    gap: '12px',
    marginTop: '12px'
  };

  const dayItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: dashboardPaint('rgba(30, 41, 59, 0.8)', 'background', false),
    borderRadius: '8px',
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.2)', 'border', false),
  };

  const timeRestrictionStyle = {
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: dashboardPaint('rgba(15, 23, 42, 0.6)', 'background', false),
    borderRadius: '12px',
    border: dashboardPaint('1px solid rgba(148, 163, 184, 0.2)', 'border', false),
  };

  return (
    <div style={containerStyle}>
      <div style={mainCard}>
        {/* Header Section */}
        <div style={headerStyle}>
          <div style={headerContent}>
            <UserPlus size={28} color={dashboardPaint("#ffffff", 'text', true)} />
            <h1 style={titleStyle}>Assign New User</h1>
          </div>
          <p style={subtitleStyle}>
            Create access credentials and configure permissions for a new user account. All times are in Eastern Time (EST/EDT). 
            <strong> New users start with NO assessments assigned by default.</strong>
          </p>
        </div>

        <div style={contentStyle}>
          {/* ✅ UPDATED: Info Box with assessment assignment clarification */}
          <div style={infoBoxStyle}>
            <Info size={20} color={dashboardPaint("#10B981", 'text')} style={{ marginTop: '2px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <p style={{ color: dashboardPaint('#a7f3d0', 'text', false), margin: '0', fontSize: '14px' }}>
                <strong>New Behavior:</strong> Users created with this form will start with <strong>NO assessments assigned</strong>. 
                Use the Assessment Settings tab to manually assign specific assessments to users after creation. 
                Configure user access permissions, validity period, time restrictions, and allowed days. Use "Block All Days" to create users with complete assessment access restrictions. 
                SubUsers can be created automatically upon user creation. All dates and times are handled in Eastern Time zone (EST/EDT).
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={formContainerStyle}>
            {error && (
              <div style={errorStyle}>
                <AlertTriangle size={20} />
                <span style={{ fontWeight: '500' }}>{error}</span>
              </div>
            )}
            {success && (
              <div style={successStyle}>
                <CheckCircle size={20} />
                <span style={{ fontWeight: '500' }}>{success}</span>
              </div>
            )}

            {/* Basic Information */}
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  <UserPlus size={16} />
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  style={inputStyle}
                  placeholder="Enter user's full name"
                  disabled={isSubmitting}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  <Mail size={16} />
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  style={inputStyle}
                  placeholder="Enter email address"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Security Information */}
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  <Shield size={16} />
                  Access Code
                </label>
                <input
                  type="text"
                  value={formData.accessCode}
                  onChange={(e) =>
                    setFormData({ ...formData, accessCode: e.target.value })
                  }
                  style={inputStyle}
                  placeholder="Enter access code"
                  maxLength={124}
                  disabled={isSubmitting}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  <Lock size={16} />
                  PIN Code
                </label>
                <input
                  type="text"
                  value={formData.pinCode}
                  onChange={(e) =>
                    setFormData({ ...formData, pinCode: e.target.value })
                  }
                  style={inputStyle}
                  placeholder="Enter 4-6 digit PIN"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            {/* Account Configuration */}
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  <Calendar size={16} />
                  Account Validity Duration (Days)
                </label>
                <input
                  type="number"
                  value={formData.validityDays}
                  onChange={(e) =>
                    setFormData({ ...formData, validityDays: e.target.value })
                  }
                  style={inputStyle}
                  placeholder="Enter validity duration in days"
                  min="1"
                  disabled={isSubmitting}
                />
                {formData.validityDays && (
                  <div style={previewBoxStyle}>
                    {formatValidityPreview()}
                  </div>
                )}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  <Users size={16} />
                  Maximum SubUsers Allowed
                </label>
                <input
                  type="number"
                  value={formData.maxSubUsers}
                  onChange={(e) =>
                    setFormData({ ...formData, maxSubUsers: Math.max(0, parseInt(e.target.value) || 5) })
                  }
                  style={inputStyle}
                  placeholder="Default: 5"
                  min="0"
                  max="100"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* SubUser Creation */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                <Users size={16} />
                Number of SubUsers to Create (Max: {formData.maxSubUsers})
              </label>
              <input
                type="number"
                value={formData.subUserCount}
                onChange={(e) => {
                  const value = Math.max(0, parseInt(e.target.value) || 0);
                  // ✅ ADDED: Cap at maxSubUsers
                  setFormData({ ...formData, subUserCount: Math.min(value, formData.maxSubUsers) });
                }}
                style={inputStyle}
                placeholder="Number of SubUsers to create (0 for none)"
                min="0"
                max={formData.maxSubUsers}
                disabled={isSubmitting}
              />
              {formData.subUserCount > 0 && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: dashboardPaint('rgba(59, 130, 246, 0.1)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(59, 130, 246, 0.1)'),
                  borderRadius: '8px',
                  border: dashboardPaint('1px solid rgba(59, 130, 246, 0.3)', 'border', true),
                  fontSize: '13px',
                  color: dashboardPaint('#93c5fd', 'text', true)
                }}>
                  <Info size={14} style={{ display: 'inline', marginRight: '6px' }} />
                  {formData.subUserCount} SubUser{formData.subUserCount !== 1 ? 's' : ''} will be created with access codes like: {formData.accessCode}-1, {formData.accessCode}-2, etc.
                </div>
              )}
            </div>

            {/* Account Status */}
            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                checked={formData.isEnabled}
                onChange={(e) =>
                  setFormData({ ...formData, isEnabled: e.target.checked })
                }
                style={checkboxStyle}
                id="isEnabled"
                disabled={isSubmitting}
              />
              <label htmlFor="isEnabled" style={{
                color: dashboardPaint('#e2e8f0', 'text', false),
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Shield size={16} />
                Account Enabled
              </label>
            </div>

            {/* Time Restrictions */}
            <div style={timeRestrictionStyle}>
              <div style={checkboxContainerStyle}>
                <input
                  type="checkbox"
                  checked={formData.restrictTime}
                  onChange={(e) =>
                    setFormData({ ...formData, restrictTime: e.target.checked })
                  }
                  style={checkboxStyle}
                  id="restrictTime"
                  disabled={isSubmitting}
                />
                <label htmlFor="restrictTime" style={{
                  color: dashboardPaint('#e2e8f0', 'text', false),
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Clock size={16} />
                  Restrict Assessment Access Time (EST)
                </label>
              </div>

              {formData.restrictTime && (
                <div style={{
                  marginTop: '16px',
                  display: 'flex',
                  flexDirection: isMobileView ? 'column' : 'row',
                  gap: '16px'
                }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>
                      Access Start Time (EST)
                    </label>
                    <input
                      type="time"
                      value={formData.accessStartTime}
                      onChange={(e) =>
                        setFormData({ ...formData, accessStartTime: e.target.value })
                      }
                      style={inputStyle}
                      required={formData.restrictTime}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>
                      Access End Time (EST)
                    </label>
                    <input
                      type="time"
                      value={formData.accessEndTime}
                      onChange={(e) =>
                        setFormData({ ...formData, accessEndTime: e.target.value })
                      }
                      style={inputStyle}
                      required={formData.restrictTime}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              {formData.restrictTime && formData.accessStartTime && formData.accessEndTime && (
                <div style={previewBoxStyle}>
                  {formatTimeRangePreview()}
                </div>
              )}
            </div>

            {/* Block All Days Option */}
            <div style={blockAllDaysContainerStyle}>
              <input
                type="checkbox"
                checked={formData.blockAllDays}
                onChange={handleBlockAllDaysChange}
                style={{
                  ...checkboxStyle,
                  accentColor: formData.blockAllDays ? '#dc2626' : '#3b82f6'
                }}
                id="blockAllDays"
                disabled={isSubmitting}
              />
              <label htmlFor="blockAllDays" style={{
                color: dashboardPaint(formData.blockAllDays ? '#f87171' : '#e2e8f0', 'text', false),
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Ban size={16} color={dashboardPaint(formData.blockAllDays ? '#f87171' : '#e2e8f0', 'text')} />
                🚫 Block Assessment Access on ALL Days (Complete Access Block)
              </label>
            </div>

            {formData.blockAllDays && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: dashboardPaint('rgba(239, 68, 68, 0.1)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(239, 68, 68, 0.1)'),
                borderRadius: '8px',
                border: dashboardPaint('1px solid rgba(239, 68, 68, 0.3)', 'border', true),
                marginBottom: '20px',
                fontSize: '13px',
                color: dashboardPaint('#f87171', 'text', true),
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertTriangle size={16} />
                <strong>WARNING:</strong> This user will be completely blocked from accessing assessments on all days. Only Admin Override can allow access.
              </div>
            )}

            {/* Allowed Days */}
            <div style={daysSectionStyle}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                flexDirection: isMobileView ? 'column' : 'row',
                gap: isMobileView ? '12px' : '0'
              }}>
                <label style={labelStyle}>
                  <Calendar size={16} />
                  Allowed Days of the Week
                </label>
                
                {!formData.blockAllDays && !isSubmitting && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexDirection: isMobileView ? 'column' : 'row'
                  }}>
                    <button
                      type="button"
                      onClick={handleSelectAllDays}
                      style={{
                        ...quickActionButtonStyle,
                        backgroundColor: dashboardPaint('rgba(16, 185, 129, 0.2)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(16, 185, 129, 0.2)'),
                        borderColor: dashboardPaint('rgba(16, 185, 129, 0.4)', 'border', true),
                        color: dashboardPaint('#34d399', 'text', true)
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = dashboardPaint('rgba(16, 185, 129, 0.3)', 'background', true);
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = dashboardPaint('rgba(16, 185, 129, 0.2)', 'background', true);
                      }}
                    >
                      <CheckCircle size={12} />
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllDays}
                      style={{
                        ...quickActionButtonStyle,
                        backgroundColor: dashboardPaint('rgba(239, 68, 68, 0.2)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(239, 68, 68, 0.2)'),
                        borderColor: dashboardPaint('rgba(239, 68, 68, 0.4)', 'border', true),
                        color: dashboardPaint('#f87171', 'text', true)
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = dashboardPaint('rgba(239, 68, 68, 0.3)', 'background', true);
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = dashboardPaint('rgba(239, 68, 68, 0.2)', 'background', true);
                      }}
                    >
                      <XCircle size={12} />
                      Block All
                    </button>
                  </div>
                )}
              </div>

              <div style={daysGridStyle}>
                {daysOfWeek.map((day) => (
                  <div key={day.value} style={dayItemStyle}>
                    <input
                      type="checkbox"
                      checked={!formData.blockAllDays && formData.daysAllowed.includes(day.value)}
                      onChange={(e) => handleDayChange(e, day.value)}
                      style={checkboxStyle}
                      id={`day-${day.value}`}
                      disabled={formData.blockAllDays || isSubmitting}
                    />
                    <label 
                      htmlFor={`day-${day.value}`} 
                      style={{ 
                        color: dashboardPaint(formData.blockAllDays ? '#64748b' : '#e2e8f0', 'text', false), 
                        fontSize: '14px',
                        cursor: (formData.blockAllDays || isSubmitting) ? 'not-allowed' : 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>

              {/* Days Access Preview */}
              <div style={getPreviewBoxStyle()}>
                {formatDaysAccessPreview()}
              </div>
            </div>

            {/* Submit button with loading state */}
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={getButtonStyle()}
              onMouseOver={(e) => {
                if (!isSubmitting) {
                  e.target.style.background = dashboardPaint('linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', 'background', true);
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseOut={(e) => {
                if (!isSubmitting) {
                  e.target.style.background = dashboardPaint('linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 'background', true);
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Creating User...
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  Assign User
                </>
              )}
            </button>

            {/* SubUser creation status */}
            {isCreatingSubUsers && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: dashboardPaint('rgba(59, 130, 246, 0.1)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('rgba(59, 130, 246, 0.1)'),
                borderRadius: '8px',
                border: dashboardPaint('1px solid rgba(59, 130, 246, 0.3)', 'border', true),
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: dashboardPaint('#93c5fd', 'text', true)
              }}>
                <Loader2 size={16} className="animate-spin" />
                <span>Creating SubUsers in background...</span>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
